import { Component } from 'react'

/**
 * ErrorBoundary
 *
 * Catches any uncaught render error inside the component tree.
 * Instead of a white screen, shows a styled recovery UI with
 * a Reload button so users can self-heal without confusion.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Caught render error:', error, info)
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#06060f',
          color: '#e2e8f0',
          fontFamily: 'system-ui, sans-serif',
          padding: '2rem',
          textAlign: 'center',
          gap: '1.5rem'
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '1rem',
            background: 'linear-gradient(135deg,#3b82f6,#10b981)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.75rem'
          }}
        >
          ⚠️
        </div>

        <div>
          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: 800,
              marginBottom: '0.5rem',
              background: 'linear-gradient(135deg,#3b82f6,#10b981)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Something went wrong
          </h1>
          <p style={{ color: '#64748b', fontSize: '0.9rem', maxWidth: 400 }}>
            An unexpected error occurred while loading the page.
            This is usually temporary — try refreshing.
          </p>
        </div>

        {import.meta.env.DEV && this.state.error && (
          <pre
            style={{
              background: 'rgba(248,113,113,0.08)',
              border: '1px solid rgba(248,113,113,0.2)',
              color: '#f87171',
              borderRadius: '0.75rem',
              padding: '1rem',
              fontSize: '0.75rem',
              maxWidth: 560,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              textAlign: 'left'
            }}
          >
            {this.state.error.message}
          </pre>
        )}

        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.6rem 1.5rem',
            borderRadius: '0.75rem',
            border: '1px solid rgba(59,130,246,0.35)',
            background: 'rgba(59,130,246,0.12)',
            color: '#818cf8',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 600,
            transition: 'all 0.2s'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(59,130,246,0.22)'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'rgba(59,130,246,0.12)'
          }}
        >
          ↺ Reload Page
        </button>
      </div>
    )
  }
}
